import moment from 'moment/moment';
import { isEmpty, isNotEmpty } from '@intelligence/v3/utils/common';
import { getNullableMoment, validateGranularity } from '@intelligence/v3/utils/date';
import { getItemApiParamsFieldMap, getItemUrlParamsFieldMap } from '../shared/fieldMapUtils';

import type { IFilterItemConfig } from '@intelligence/v3/filterCore/types';
import type { IRangePickerFilterValue, IRangePickerMiddlewareParams, IRangePickerControlProps } from './types';

export const DEFAULT_RANGE_PICKER_TYPE_LIST = ['date', 'week', 'month'];

export const GRANULARITY_TO_TYPE: Record<IGranularity, moment.unitOfTime.StartOf> = {
  daily: 'date',
  weekly: 'week',
  monthly: 'month',
  quarterly: 'quarter',
  yearly: 'year',
};

export const TYPE_TO_GRANULARITY: Record<string, IGranularity> = {
  date: 'daily',
  week: 'weekly',
  month: 'monthly',
  quarter: 'quarterly',
  year: 'yearly',
};

export const getGranularityByType = (type = 'date'): IGranularity => {
  return TYPE_TO_GRANULARITY[type] || 'daily';
};

export const getTypeByGranularity = (granularity = 'daily'): string => {
  return GRANULARITY_TO_TYPE[granularity as IGranularity] || 'date';
};

const isMatchedReleaseFieldPair = (
  fieldMap: IRecord<string> | null | undefined,
  startDateField: string,
  endDateField: string,
): boolean => {
  return (
    String(fieldMap?.startDate || '').toLowerCase() === startDateField.toLowerCase() &&
    String(fieldMap?.endDate || '').toLowerCase() === endDateField.toLowerCase()
  );
};

export const isReleaseRangePickerItem = (item?: IFilterItemConfig | null): boolean => {
  if (isEmpty(item)) {
    return false;
  }

  const middlewareParams = getRangePickerMiddlewareParams(item);

  return (
    isMatchedReleaseFieldPair(getItemApiParamsFieldMap(item, middlewareParams), 'release_start_date', 'release_end_date') ||
    isMatchedReleaseFieldPair(getItemApiParamsFieldMap(item, middlewareParams), 'start_release_date', 'end_release_date') ||
    isMatchedReleaseFieldPair(getItemUrlParamsFieldMap(item, middlewareParams), 'releaseStartDate', 'releaseEndDate') ||
    isMatchedReleaseFieldPair(getItemUrlParamsFieldMap(item, middlewareParams), 'startReleaseDate', 'endReleaseDate')
  );
};

const getNormalizedStartOfWeek = (startOfWeek?: number): number => {
  if (!Number.isInteger(startOfWeek)) {
    return 0;
  }

  return (((startOfWeek as number) % 7) + 7) % 7;
};

export const alignMomentToGranularity = (
  granularity: IGranularity,
  boundary: 'start' | 'end',
  startOfWeek = 0,
  dateM: moment.Moment,
): moment.Moment => {
  if (granularity === 'weekly') {
    const normalizedStartOfWeek = getNormalizedStartOfWeek(startOfWeek);
    const diff = (dateM.day() - normalizedStartOfWeek + 7) % 7;
    const weekStartDateM = dateM.clone().subtract(diff, 'days').startOf('day');

    return boundary === 'start' ? weekStartDateM : weekStartDateM.clone().add(6, 'days').endOf('day');
  }

  const granularityUnit = GRANULARITY_TO_TYPE[granularity] || 'date';

  return boundary === 'start' ? dateM.clone().startOf(granularityUnit) : dateM.clone().endOf(granularityUnit);
};

export const getRangePickerBoundaryValue = (params: {
  granularity: IGranularity;
  startDate: string;
  endDate: string;
  startOfWeek?: number;
}): IRangePickerFilterValue => {
  const { granularity, startDate, endDate, startOfWeek = 0 } = params;

  return {
    granularity,
    startDate: alignMomentToGranularity(granularity, 'start', startOfWeek, moment(startDate)).format('YYYY-MM-DD'),
    endDate: alignMomentToGranularity(granularity, 'end', startOfWeek, moment(endDate)).format('YYYY-MM-DD'),
  };
};

export const getValidatedRangePickerValue = (params: {
  granularity: IGranularity;
  startDate: string;
  endDate: string;
  granularities?: IGranularity[];
  startOfWeek?: number;
}): IRangePickerFilterValue | null => {
  const { granularity, startDate, endDate, granularities, startOfWeek } = params;

  if (!validateGranularity(granularities, granularity)) {
    return null;
  }

  const startDateM = getNullableMoment(startDate);
  const endDateM = getNullableMoment(endDate);
  if (startDateM === null || endDateM === null) {
    return null;
  }

  return getRangePickerBoundaryValue({
    granularity,
    startDate,
    endDate,
    startOfWeek,
  });
};

export const getRangePickerBoundaryValueByType = (params: {
  type: string;
  startDate: string;
  endDate: string;
  startOfWeek?: number;
}): IRangePickerFilterValue => {
  const { type, startDate, endDate, startOfWeek = 0 } = params;

  return getRangePickerBoundaryValue({
    granularity: getGranularityByType(type),
    startDate,
    endDate,
    startOfWeek,
  });
};

export const getRangePickerDefaultComponentValue = (startOfWeek = 0): Record<string, [string, string]> => {
  const dailyStart = moment().subtract(30, 'days').format('YYYY-MM-DD');
  const dailyEnd = moment().subtract(1, 'days').format('YYYY-MM-DD');
  const weeklyValue = getRangePickerBoundaryValue({
    granularity: 'weekly',
    startDate: moment().subtract(12, 'weeks').format('YYYY-MM-DD'),
    endDate: moment().subtract(1, 'weeks').format('YYYY-MM-DD'),
    startOfWeek,
  });
  const monthlyValue = getRangePickerBoundaryValue({
    granularity: 'monthly',
    startDate: moment().subtract(12, 'months').format('YYYY-MM-DD'),
    endDate: moment().subtract(1, 'months').format('YYYY-MM-DD'),
    startOfWeek,
  });

  return {
    daily: [dailyStart, dailyEnd],
    weekly: [weeklyValue.startDate, weeklyValue.endDate],
    monthly: [monthlyValue.startDate, monthlyValue.endDate],
  };
};

export const normalizeRangePickerValueByItem = (
  item: IFilterItemConfig,
  value?: unknown,
): IRangePickerFilterValue | undefined => {
  const rangeValue = getRangePickerValue(value);
  const middlewareParams = getRangePickerMiddlewareParams(item);
  const { granularity, startDate, endDate } = rangeValue;

  const normalizedValue = getValidatedRangePickerValue({
    granularity,
    startDate,
    endDate,
    granularities: middlewareParams?.granularities,
    startOfWeek: middlewareParams?.startOfWeek,
  });

  if (normalizedValue) {
    return normalizedValue;
  }

  if (value !== undefined && value !== null) {
    return undefined;
  }

  return rangeValue;
};

export const getRangePickerValue = (value?: unknown): IRangePickerFilterValue => {
  const rangeValue = value as IRangePickerFilterValue | undefined;

  return {
    granularity: rangeValue?.granularity || 'daily',
    startDate: rangeValue?.startDate || '',
    endDate: rangeValue?.endDate || '',
  };
};

export const getRangePickerMiddlewareParams = (item?: IFilterItemConfig | null): IRangePickerMiddlewareParams => {
  if (isEmpty(item)) {
    return { granularities: [], startOfWeek: 0 };
  }

  const controlProps = item?.controlProps as IRangePickerControlProps | undefined;
  const typeList: string[] = isEmpty(controlProps?.typeList)
    ? DEFAULT_RANGE_PICKER_TYPE_LIST
    : controlProps?.typeList || [];
  const granularities = typeList
    .map((itemType) => {
      return TYPE_TO_GRANULARITY[itemType];
    })
    .filter(isNotEmpty);

  return {
    granularities,
    startOfWeek: controlProps?.startOfWeek ?? 0,
  };
};
